import React, { useEffect, useState } from 'react';
import {
  Eye,
  EyeOff,
  MoreHorizontal,
} from 'lucide-react';
import Loader from '../../Core/Components/Loader/Loader';
import { useAuth } from '../../Core/Context/AuthContext';
import { EXCEPTION_STATUS_CODES } from '../../../constants/applicationConstants';
import { callApi } from '../../../Utils/Api/Api';
import { useNavigate, useSearchParams } from 'react-router-dom';
import MicrosoftLogo from '../../../assets/logo/microsoft-logo.png';
import GoogleLogo from '../../../assets/logo/google@72x.png';
import OktaLogo from '../../../assets/logo/okta-logo.svg';

const dashboardPath = '/';

const initialFormValues = {
  username: '',
  password: '',
};

const getValidationErrors = (formValues) => {
  const errors = {};

  if (!formValues.username?.trim()) {
    errors.username = 'Username is required.';
  }

  if (!formValues.password?.trim()) {
    errors.password = 'Password is required.';
  }

  return errors;
};

const SocialLoginButton = ({
  icon,
  label,
  onClick,
}) => (
  <button
    type="button"
    onClick={onClick}
    className="flex h-[46px] w-full items-center justify-center gap-3 rounded-[4px] border border-[#555555] bg-white px-4 text-[14px] font-normal uppercase tracking-[0] text-[#374151] transition-colors duration-200 ease-in-out hover:border-[#2f2f2f] focus:outline-none focus:ring-2 focus:ring-[#ff6a2a]/30"
  >
    <img src={icon} alt="" className="h-[18px] w-[18px] object-contain" />
    <span>{label}</span>
  </button>
);

const getSafeRedirectPath = (value) => {
  if (typeof value !== 'string' || !value.trim()) {
    return dashboardPath;
  }

  return value.startsWith('/') && !value.startsWith('//')
    ? value
    : dashboardPath;
};

const LoginForm = ({ isLoader, setIsLoader }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { checkTokens, completeLogin } = useAuth();
  const [formValues, setFormValues] = useState(initialFormValues);
  const [errors, setErrors] = useState({});
  const [isAuthProviderLoading, setIsAuthProviderLoading] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [authProviders, setAuthProviders] = useState({
    AUTH_AZURE: '0',
    AUTH_GOOGLE: '0',
    AUTH_OKTA: '0',
    AUTH_OKTA: '0',
    AUTH_OPENID: '0',
  });
  const [alertMessage, setAlertMessage] = useState('');
  const redirectTo = getSafeRedirectPath(searchParams.get('redirectTo'));

  useEffect(() => {
    if (checkTokens()) {
      navigate(redirectTo, { replace: true });
    }
  }, [checkTokens, navigate, redirectTo]);

  useEffect(() => {
    const fetchAuthProviders = async () => {
      try {
        const response = await callApi('/auth/providers', 'GET');
        setAuthProviders(response);
      } catch (error) {
        console.error('Error fetching auth providers:', error);
      } finally {
        setIsAuthProviderLoading(false);
      }
    };

    fetchAuthProviders();
  }, []);

  const hasSocialProviders =
    authProviders.AUTH_AZURE === '1' ||
    authProviders.AUTH_GOOGLE === '1' ||
    authProviders.AUTH_OKTA === '1';

  const handleFieldChange = (event) => {
    const { name, value } = event.target;

    setFormValues((currentValues) => ({
      ...currentValues,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((currentErrors) => {
        const nextErrors = { ...currentErrors };
        delete nextErrors[name];
        return nextErrors;
      });
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const nextErrors = getValidationErrors(formValues);
    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    setAlertMessage('');
    setIsLoader(true);

    const dataItem = {
      ...formValues,
      device_id: navigator.userAgent,
      browser: navigator.userAgent,
      os: navigator.platform,
    };

    try {
      const response = await callApi('/login', 'POST', dataItem);
      if (response?.isSuccess) {
        const data = response.payload;
        completeLogin(data);
        window.location.replace(redirectTo);
      } else if (
        response?.statusCode?.code &&
        !EXCEPTION_STATUS_CODES.includes(response?.statusCode?.code)
      ) {
        setAlertMessage(response?.message || 'Unable to sign in. Please try again.');
      } else {
        setAlertMessage('Unable to sign in. Please try again.');
      }
    } catch (error) {
      console.error('Login error:', error);
      setAlertMessage('Unable to sign in. Please try again.');
    } finally {
      setIsLoader(false);
    }
  };

  const handleSocialLogin = async (driver) => {
    setAlertMessage('');
    setIsLoader(true);

    try {
      const response = await callApi(`/social/auth/URL?driver=${driver}`, 'GET');

      if (response?.isSuccess) {
        window.location.href = response.payload.scalar;
      } else {
        setAlertMessage('Failed to get social login URL. Please try again.');
      }
    } catch (error) {
      console.error('Get social login URL error:', error);
      setAlertMessage('Failed to get social login URL. Please try again.');
    } finally {
      setIsLoader(false);
    }
  };

  const getInputClasses = (fieldName) => {
    const baseClasses =
      'h-11 w-full rounded-[8px] border bg-[#dfe7f4] px-4 pr-20 text-[14px] font-normal text-[#374151] outline-none transition duration-200 ease-in-out placeholder:text-[#6b7280] focus:border-[#ff6a2a] focus:ring-2 focus:ring-[#ff6a2a]/20';

    return errors[fieldName]
      ? `${baseClasses} border-[#ef4444]`
      : `${baseClasses} border-[#b9c2cf]`;
  };

  return (
    <div className="w-full">
      {alertMessage && (
        <div className="mb-4 rounded-[8px] border border-[#ef4444] bg-[#fef2f2] px-4 py-3 text-[13px] text-[#b91c1c]">
          {alertMessage}
        </div>
      )}

      <form onSubmit={handleSubmit} className="w-full">
        <div className="space-y-4">
          <div className="w-full">
            <label
              htmlFor="username"
              className="mb-2 block text-[14px] font-bold leading-[1.4] text-[#374151]"
            >
              Username <span className="text-[#ef4444]">*</span>
            </label>
            <div className="relative w-full">
              <input
                id="username"
                name="username"
                type="text"
                value={formValues.username}
                onChange={handleFieldChange}
                className={getInputClasses('username')}
                autoComplete="username"
              />
              <span className="pointer-events-none absolute right-4 top-1/2 inline-flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-[4px] bg-[#b9c2cf] text-[#ffffff]">
                <MoreHorizontal size={14} />
              </span>
            </div>
            {errors.username && (
              <p className="mt-1 text-[12px] leading-[1.4] text-[#ef4444]">
                {errors.username}
              </p>
            )}
          </div>

          <div className="w-full">
            <label
              htmlFor="password"
              className="mb-2 block text-[14px] font-bold leading-[1.4] text-[#374151]"
            >
              Password <span className="text-[#ef4444]">*</span>
            </label>
            <div className="relative w-full">
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={formValues.password}
                onChange={handleFieldChange}
                className={getInputClasses('password')}
                autoComplete="current-password"
              />
              <span className="pointer-events-none absolute right-12 top-1/2 inline-flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-[4px] bg-[#b9c2cf] text-[#ffffff]">
                <MoreHorizontal size={14} />
              </span>
              <button
                type="button"
                onClick={() => setShowPassword((currentValue) => !currentValue)}
                className="absolute right-4 top-1/2 inline-flex -translate-y-1/2 items-center justify-center text-[#6b7280] transition-colors duration-200 ease-in-out hover:text-[#374151] focus:outline-none focus:ring-2 focus:ring-[#ff6a2a]/30"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {errors.password && (
              <p className="mt-1 text-[12px] leading-[1.4] text-[#ef4444]">
                {errors.password}
              </p>
            )}
          </div>
        </div>

        <button
          type="submit"
          disabled={isLoader}
          className="mt-[20px] h-[42px] w-full rounded-[4px] bg-[#4a4a4a] text-[14px] font-medium uppercase text-white transition duration-200 ease-in-out hover:bg-[#3f3f3f] disabled:opacity-60"
        >
          Login
        </button>
      </form>

      {!isAuthProviderLoading && hasSocialProviders && (
        <div className="my-6 h-px w-full bg-[#a7a7a7]" aria-hidden="true" />
      )}

      <div className="space-y-[10px]">
        {isAuthProviderLoading && (
          <div className="flex justify-center py-3">
            <Loader />
          </div>
        )}

        {!isAuthProviderLoading && authProviders.AUTH_AZURE === '1' && (
          <SocialLoginButton
            icon={MicrosoftLogo}
            label="SIGN IN WITH MICROSOFT"
            onClick={() => handleSocialLogin('azure')}
          />
        )}

        {!isAuthProviderLoading && authProviders.AUTH_GOOGLE === '1' && (
          <SocialLoginButton
            icon={GoogleLogo}
            label="SIGN IN WITH GOOGLE"
            onClick={() => handleSocialLogin('google')}
          />
        )}

        {!isAuthProviderLoading && authProviders.AUTH_OKTA === '1' && (
          <SocialLoginButton
            icon={OktaLogo}
            label="SIGN IN WITH OPENID"
            onClick={() => handleSocialLogin('okta')}
          />
        )}
      </div>
    </div>
  );
};

export default LoginForm;
